import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DailyIframe from "@daily-co/daily-js";
import { VideoCameraIcon as VideoCamera, MicrophoneIcon as Microphone, MicrophoneSlashIcon as MicrophoneSlash, VideoCameraSlashIcon as VideoCameraSlash, PhoneSlashIcon as PhoneSlash, ArrowLeftIcon as ArrowLeft, WarningIcon as Warning, HourglassIcon as Hourglass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";

/**
 * `/tours/:id/room` — the live tour room.
 *
 * Flow:
 *  1. On mount, fetch /api/video-tours/{id}/room → {room_url, token, mocked, expires_at}.
 *  2. Build a Daily call frame (DailyIframe.createFrame) into the container ref.
 *  3. join() with the room URL + token. Seller is owner (can eject; not exposed in UI).
 *  4. Provide local mic/camera toggles + a hangup button that triggers leave + redirect.
 *  5. Show a banner when the room is mocked (no DAILY_API_KEY configured) so the
 *     user understands why the iframe shows Daily's "missing room" placeholder.
 */
export default function VideoTourRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const callRef = useRef(null);
  const [info, setInfo] = useState(null); // {room_url, token, mocked, expires_at}
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchRoom() {
      try {
        const r = await api.get(`/video-tours/${id}/room`);
        if (!cancelled) setInfo(r.data);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 425) {
          setError({ kind: "early", detail: typeof err.response.data?.detail === "string" ? err.response.data.detail : "The room opens 5 minutes before the scheduled time." });
        } else if (status === 410) {
          setError({ kind: "expired", detail: "The tour window has closed." });
        } else if (status === 400) {
          setError({ kind: "not-ready", detail: typeof err.response.data?.detail === "string" ? err.response.data.detail : "Tour is not yet ready to join." });
        } else {
          setError({ kind: "generic", detail: formatApiError(err) });
        }
      }
    }
    fetchRoom();
    return () => { cancelled = true; };
  }, [id]);

  const startCall = async () => {
    if (!info || !containerRef.current) return;
    setJoining(true);
    try {
      // Tear down any pre-existing frame (hot-reload safety).
      if (callRef.current) {
        await callRef.current.destroy();
        callRef.current = null;
      }
      const frame = DailyIframe.createFrame(containerRef.current, {
        showLeaveButton: false,
        iframeStyle: {
          width: "100%",
          height: "100%",
          border: "0",
          borderRadius: "12px",
        },
      });
      callRef.current = frame;
      frame.on("joined-meeting", () => setJoined(true));
      frame.on("left-meeting", () => {
        setJoined(false);
      });
      frame.on("error", (e) => {
        console.error("Daily error:", e);
        toast.error(e?.errorMsg || "Video call error");
      });
      const joinOpts = { url: info.room_url };
      if (info.token) joinOpts.token = info.token;
      if (info.user_name) joinOpts.userName = info.user_name;
      await frame.join(joinOpts);
    } catch (err) {
      console.error(err);
      toast.error("Could not join the call. Please refresh and try again.");
    } finally {
      setJoining(false);
    }
  };

  const toggleMic = async () => {
    if (!callRef.current) return;
    const next = !micOn;
    await callRef.current.setLocalAudio(next);
    setMicOn(next);
  };

  const toggleCam = async () => {
    if (!callRef.current) return;
    const next = !camOn;
    await callRef.current.setLocalVideo(next);
    setCamOn(next);
  };

  const hangup = async () => {
    if (callRef.current) {
      try { await callRef.current.leave(); } catch {}
      try { await callRef.current.destroy(); } catch {}
      callRef.current = null;
    }
    navigate("/tours");
  };

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (callRef.current) {
        callRef.current.destroy().catch(() => {});
        callRef.current = null;
      }
    };
  }, []);

  // Auto-hangup when the room expires. Daily.co server-enforces `exp`
  // and ejects participants, but the iframe can sit on a closed room
  // silently — explicitly tear down + bounce the user to /tours so
  // they get a clear "tour ended" toast.
  useEffect(() => {
    if (!info?.expires_at) return;
    const ms = new Date(info.expires_at).getTime() - Date.now();
    if (ms <= 0) return; // already past — let the next click hit the 410 path
    const t = setTimeout(async () => {
      if (callRef.current) {
        try { await callRef.current.leave(); } catch {}
        try { await callRef.current.destroy(); } catch {}
        callRef.current = null;
      }
      toast("Your 15-minute tour window has ended.", { duration: 6000 });
      navigate("/tours");
    }, ms);
    return () => clearTimeout(t);
  }, [info?.expires_at, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0E0E0E] text-white flex flex-col items-center justify-center px-6" data-testid="tour-room-error">
        <Warning size={36} weight="duotone" className="text-gold mb-3" />
        <h1 className="font-spectral text-3xl mb-2 text-center">
          {error.kind === "early" ? "Almost ready" : error.kind === "expired" ? "Tour ended" : "Can't join yet"}
        </h1>
        <p className="text-[14px] text-white/75 max-w-lg text-center mb-6">{error.detail}</p>
        <Button asChild className="rounded-full bg-white text-ink hover:bg-[#C9A227]">
          <Link to="/tours" data-testid="tour-room-back-link">
            <ArrowLeft size={14} className="mr-1.5" />
            Back to my tours
          </Link>
        </Button>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-[#0E0E0E] text-white flex items-center justify-center" data-testid="tour-room-loading">
        <Hourglass size={36} weight="duotone" className="text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0E0E0E] text-white flex flex-col" data-testid="tour-room">
      <header className="px-4 py-3 flex items-center justify-between border-b border-white/10">
        <Link to="/tours" className="text-[12px] text-white/70 hover:text-white inline-flex items-center gap-1.5">
          <ArrowLeft size={12} /> Back to tours
        </Link>
        <p className="text-[11px] text-white/60 uppercase tracking-[0.2em] font-bold flex items-center gap-2">
          <VideoCamera size={13} weight="duotone" className="text-gold" />
          Live tour
          {info.mocked && (
            <span className="bg-[#FFD37D]/20 border border-[#FFD37D] text-[#FFD37D] rounded-full px-2 py-0.5 text-[9px]">
              MOCK MODE
            </span>
          )}
        </p>
        <div className="text-[11px] text-white/60">
          Ends {new Date(info.expires_at).toLocaleTimeString()}
        </div>
      </header>

      {info.mocked && (
        <div className="px-4 py-2 bg-[#3F2A00] text-[#FFD37D] text-[12px] flex items-center gap-2" data-testid="tour-mock-banner">
          <Warning size={13} weight="fill" />
          Daily.co API key isn't configured yet — this is a placeholder room. Add
          DAILY_API_KEY + DAILY_DOMAIN in admin → Launch readiness to enable real video.
        </div>
      )}

      <div className="flex-1 p-4 flex flex-col">
        <div
          ref={containerRef}
          className="flex-1 bg-black rounded-xl overflow-hidden min-h-[400px]"
          data-testid="tour-frame-container"
        >
          {!joined && !joining && (
            <div className="h-full flex flex-col items-center justify-center gap-4">
              <VideoCamera size={48} weight="duotone" className="text-gold" />
              <p className="text-white/85 text-center max-w-sm">
                Ready when you are. Make sure your camera and mic are
                allowed in your browser, then tap Join below.
              </p>
              <Button
                onClick={startCall}
                size="lg"
                className="rounded-full bg-[#C9A227] hover:bg-[#FFD37D] text-ink font-bold"
                data-testid="tour-room-join-btn"
              >
                <VideoCamera size={16} weight="duotone" className="mr-2" />
                Join the call
              </Button>
            </div>
          )}
          {joining && (
            <div className="h-full flex items-center justify-center">
              <Hourglass size={36} weight="duotone" className="text-gold animate-spin" />
            </div>
          )}
        </div>

        {joined && (
          <div className="mt-4 flex items-center justify-center gap-3 flex-wrap" data-testid="tour-controls">
            <Button
              onClick={toggleMic}
              size="lg"
              variant="outline"
              className={`rounded-full ${micOn ? "bg-white/10 text-white border-white/20" : "bg-[#C1272D] text-white border-[#C1272D]"}`}
              data-testid="tour-toggle-mic"
            >
              {micOn ? <Microphone size={16} weight="duotone" /> : <MicrophoneSlash size={16} weight="fill" />}
              <span className="ml-2">{micOn ? "Mute" : "Unmute"}</span>
            </Button>
            <Button
              onClick={toggleCam}
              size="lg"
              variant="outline"
              className={`rounded-full ${camOn ? "bg-white/10 text-white border-white/20" : "bg-[#C1272D] text-white border-[#C1272D]"}`}
              data-testid="tour-toggle-cam"
            >
              {camOn ? <VideoCamera size={16} weight="duotone" /> : <VideoCameraSlash size={16} weight="fill" />}
              <span className="ml-2">{camOn ? "Stop video" : "Start video"}</span>
            </Button>
            <Button
              onClick={hangup}
              size="lg"
              className="rounded-full bg-[#C1272D] hover:bg-[#a02124] text-white"
              data-testid="tour-hangup-btn"
            >
              <PhoneSlash size={16} weight="duotone" />
              <span className="ml-2">Leave</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
